/**
 * Product Bank platform from shop URL.
 * Matches dashboard ProductBank.getPlatformFromShopLink (a.co, amzn.*, affiliate unwrap).
 */
export function getPlatformFromShopLink(
  shopLink: string | null | undefined
): string | null {
  if (!shopLink || typeof shopLink !== "string") return null;
  const raw = shopLink.trim();
  if (!raw) return null;
  let href = raw;
  if (!/^https?:\/\//i.test(href)) href = `https://${href}`;
  try {
    const url = new URL(href);
    const host = url.hostname.replace(/^www\./, "").toLowerCase();
    const path = url.pathname.toLowerCase();
    const hrefLower = href.toLowerCase();

    const redirectParams = [
      "u",
      "url",
      "target",
      "redirect",
      "destination",
      "link",
    ];
    const isRedirectHost =
      host === "l.instagram.com" ||
      host === "l.facebook.com" ||
      /^link\.(instagram|facebook)\.com$/i.test(host) ||
      host === "go.redirectingat.com" ||
      host.endsWith(".redirectingat.com");
    if (isRedirectHost) {
      for (const param of redirectParams) {
        const encoded = url.searchParams.get(param);
        if (encoded) {
          try {
            const decoded = decodeURIComponent(encoded);
            if (/^https?:\/\//i.test(decoded)) {
              const inner = getPlatformFromShopLink(decoded);
              if (inner) return inner;
            }
          } catch {
            // ignore decode errors
          }
        }
      }
    }

    for (const param of ["u", "url", "dest", "redirect_url", "redirect"]) {
      const encoded = url.searchParams.get(param);
      if (!encoded) continue;
      try {
        const decoded = decodeURIComponent(encoded);
        if (
          /^https?:\/\//i.test(decoded) &&
          /amazon\.|amzn\.|a\.co\//i.test(decoded)
        ) {
          return "Amazon";
        }
        if (/^https?:\/\//i.test(decoded)) {
          const inner = getPlatformFromShopLink(decoded);
          if (inner) return inner;
        }
      } catch {
        // ignore
      }
    }

    if (
      host === "go.shopmy.us" ||
      host === "shopmy.us" ||
      host.endsWith(".shopmy.us")
    ) {
      return "ShopMy";
    }
    if (
      host === "ltk.com" ||
      host === "shop.ltk.com" ||
      host.endsWith(".ltk.com") ||
      host === "shopltk.com" ||
      host.endsWith(".shopltk.com") ||
      host === "liketoknow.it" ||
      host.endsWith(".liketoknow.it") ||
      host === "ltk.app" ||
      host.endsWith(".ltk.app") ||
      host === "ltk.app.link" ||
      host.endsWith(".ltk.app.link") ||
      host === "like.to" ||
      host.endsWith(".like.to")
    ) {
      return "LTK";
    }

    const isAmazonHost =
      host === "amazon" ||
      host.includes("amazon.") ||
      host.startsWith("amzn.") ||
      host === "amzn.to" ||
      host === "amzn.com" ||
      host.endsWith(".amzn.to") ||
      host === "a.co" ||
      host.endsWith(".a.co") ||
      host === "amzlink.to" ||
      host.endsWith(".amzlink.to");
    const isAmazonPathOrHref =
      path.includes("/amzn/") ||
      /(?:^|[/.])amazon\./i.test(hrefLower) ||
      /(?:^|[/.])amzn\./i.test(hrefLower) ||
      hrefLower.includes("://a.co/") ||
      hrefLower.includes("://www.a.co/");

    if (isAmazonHost || isAmazonPathOrHref) {
      return "Amazon";
    }
    if (host.includes("being-caroline.com") && path.includes("amzn")) {
      return "Amazon";
    }
    return null;
  } catch {
    const lower = raw.toLowerCase();
    if (
      lower.includes("amazon.") ||
      lower.includes("amzn.") ||
      lower.includes("a.co/")
    ) {
      return "Amazon";
    }
    return null;
  }
}

export function resolveProductBankPlatformFromUrl(
  shopLink: string | null | undefined
): string {
  return getPlatformFromShopLink(shopLink) || "MISC";
}

/** Trim + add https:// when creators paste bare amazon.com / amzn.to links. */
export function normalizeHttpUrlInput(raw: string | null | undefined): string {
  const value = String(raw || "").trim();
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  return `https://${value}`;
}

/**
 * True for storefront, product, short-link, and regional Amazon URLs
 * (amazon.com / .co.uk / .in, amzn.to, a.co, …).
 */
export function isAmazonAffiliateUrl(raw: string | null | undefined): boolean {
  return getPlatformFromShopLink(normalizeHttpUrlInput(raw)) === "Amazon";
}

/** True for shopmy.us / go.shopmy.us storefront URLs (not Shopify shop.app). */
export function isShopMyAffiliateUrl(raw: string | null | undefined): boolean {
  return getPlatformFromShopLink(normalizeHttpUrlInput(raw)) === "ShopMy";
}

/**
 * True for LTK / LikeToKnowIt profile URLs (shopltk.com, liketoknow.it, …).
 * Rejects ShopMy, Amazon, and other affiliate hosts.
 */
export function isLtkAffiliateUrl(raw: string | null | undefined): boolean {
  return getPlatformFromShopLink(normalizeHttpUrlInput(raw)) === "LTK";
}

/**
 * Platforms allowed in Product Bank "Filter By Platform".
 * Domain fragments (a, airbnb, amzlink, …) from the legacy
 * `get_productbank_plateforrmm` RPC are rejected here so they never
 * surface as chips even if that RPC is wired up again by mistake.
 *
 * Keys stay as returned (except MISC collapse) so content RPCs keep matching.
 */
const PRODUCT_BANK_PLATFORM_FILTER_ALLOWLIST = new Set([
  "amazon",
  "ltk",
  "shopmy",
  "shop",
  "instagram",
  "youtube",
  "tiktok",
  "pinterest",
  "newsletter",
  "linkedin",
  "twitter",
  "x",
  "podcast",
  "discord",
  "snapchat",
  "spotify",
  "applepodcast",
  "misc",
]);

export function isProductBankPlatformFilterValue(raw: unknown): boolean {
  if (raw == null) return false;
  const lower = String(raw).trim().toLowerCase();
  if (!lower) return false;
  if (lower === "misc" || lower.startsWith("misc-") || lower === "added by me") {
    return true;
  }
  return PRODUCT_BANK_PLATFORM_FILTER_ALLOWLIST.has(lower);
}
